<template>
	<div class="request-detail-page show-scrollbar" v-if="ticket">
		<header class="request-detail-header">
			<button type="button" class="request-back" @click="$emit('cancel')">
				<portal-icon name="arrow-left" :size="16"></portal-icon>
				{{ $t('requests.page-heading') }}
			</button>

			<div class="request-title-row">
				<div class="request-title">
					<span class="request-number">{{ ticket.ticketNumber }}</span>
					<h1>{{ ticket.subject }}</h1>
				</div>
				<portal-status-pill
					v-if="ticket.open"
					:background="ticketStatusBackground(ticket, ticketTypes)"
					color="var(--portal-text)"
				>
					{{ ticket.status }}
				</portal-status-pill>
				<portal-status-pill
					v-else
					background="var(--v-alert_10-base)"
					color="var(--v-alert-base)"
				>
					{{ $t('requests.closed') }}
				</portal-status-pill>
			</div>
		</header>

		<div class="request-detail-grid">
			<main class="request-conversation">
				<section class="comment-composer">
					<label class="composer-label" for="request-comment">{{ $t('requests.comment') }}</label>
					<v-textarea
						id="request-comment"
						rows="5"
						v-model="comment"
						dense
						outlined
						persistent-placeholder
						hide-details
					></v-textarea>
					<div class="composer-footer">
						<portal-file-input
							class="composer-file-input"
							v-model="attachments"
							:label="$t('requests.attachments')"
							:placeholder="$t('requests.upload-attachments')"
							:remove-label="$t('global.delete')"
						></portal-file-input>
						<button type="button" class="add-comment-action" :disabled="!comment" @click="addComment()">
							{{ $t('requests.add-comment') }}
						</button>
					</div>
				</section>

				<section class="comment-stream" v-if="comments.length">
					<article class="comment-card" v-for="ticketComment in comments" :key="ticketComment.id">
						<header class="comment-header">
							<span class="comment-avatar">{{ commenterInitials(ticketComment.commentBy) }}</span>
							<div>
								<strong>{{ getCommentBy(ticketComment.commentBy) }}</strong>
								<span>
									{{ DateTime.fromISO(ticketComment.created).toLocaleString(DateTime.DATETIME_FULL) }}
								</span>
								<span v-if="ticketComment.updated">
									{{ $t('requests.existing.updated') }}
									{{ DateTime.fromISO(ticketComment.updated).toLocaleString(DateTime.DATETIME_FULL) }}
								</span>
							</div>
						</header>
						<div
							class="comment-body"
							v-if="ticketComment.commentFormat === 'PlainText'"
						>
							{{ ticketComment.comment }}
						</div>
						<div class="comment-body rich-comment" v-else v-html="ticketComment.comment"></div>
						<div
							v-if="ticketComment.attachments && ticketComment.attachments.length"
							class="comment-attachments"
						>
							<button
								v-for="attachment in ticketComment.attachments"
									:key="attachment.fileName"
								type="button"
								class="comment-attachment"
									@click="downloadFile(attachment)"
								>
								<portal-icon name="paperclip" :size="14"></portal-icon>
								{{ attachment.fileName }}
							</button>
						</div>
					</article>
				</section>
			</main>

			<aside class="request-sidebar">
				<section class="request-meta-card">
					<div class="request-meta-row">
						<span>{{ $t('requests.existing.requester') }}</span>
						<strong v-if="requester">{{ requester.firstName }} {{ requester.lastName }}</strong>
						<strong v-else>—</strong>
					</div>
					<div class="request-meta-row">
						<span>{{ $t('requests.existing.type') }}</span>
						<strong>{{ ticketType.type || '—' }}</strong>
					</div>
					<div class="request-meta-row">
						<span>{{ $t('requests.existing.created') }}</span>
						<strong>{{ DateTime.fromISO(ticket.created).toLocaleString(DateTime.DATETIME_MED) }}</strong>
					</div>
					<div class="request-meta-row">
						<span>{{ $t('requests.existing.updated') }}</span>
						<strong>{{ DateTime.fromISO(ticket.updated).toLocaleString(DateTime.DATETIME_MED) }}</strong>
					</div>
				</section>

				<section class="request-answer-section" v-if="hasFormAnswers">
					<button
						type="button"
						class="request-answer-toggle"
						:aria-expanded="showFormAnswers ? 'true' : 'false'"
						@click="showFormAnswers = !showFormAnswers"
					>
						<span>{{ $t('requests.new.details') }}</span>
						<span class="request-answer-count">{{ ticket.formData.answers.length }}</span>
						<portal-icon
							:name="showFormAnswers ? 'chevron-up' : 'chevron-down'"
							:size="17"
						></portal-icon>
					</button>
					<div class="request-answer-card" v-show="showFormAnswers">
						<div
							v-for="(answer, index) in ticket.formData.answers"
							:key="index"
							class="request-answer"
						>
							<span>{{ questionText(answer.question) }}</span>
							<strong class="text-pre-wrap">{{ answer.answer }}</strong>
						</div>
					</div>
				</section>
			</aside>
		</div>
	</div>
</template>

<script>
	import TicketService from '@/modules/tickets/TicketService';
	import { ticketStatusBackground } from '@/modules/tickets/ticketStatus';
	import { DateTime } from 'luxon';
	import ClientService from '@/modules/client/ClientService';
	import ProjectService from '@/modules/projects/ProjectService';
	import PortalFileInput from '@/components/PortalFileInput';
	import { questionText } from '@/modules/forms/v2/schema/questionText';
	import axios from 'axios';

	export default {
		name: 'ExistingTicket',

		props: ['id', 'ticketTypes'],

		components: { PortalFileInput },

		data: function() {
			return {
				ticket: null,
				comments: [],
				contacts: [],
				comment: null,
				attachments: [],
				users: [],
				showFormAnswers: false,
				ticketService: new TicketService(),
				clientService: new ClientService(),
				projectService: new ProjectService(),
				DateTime: DateTime,
			};
		},

		mounted() {
			this.initialize();
      this.setupTicketListeners();
		},

		beforeDestroy() {
      this.stopTicketListeners();
    },

		methods: {
			questionText,
			ticketStatusBackground,
			initialize: function() {
				this.getTicketDetail();
				this.getContacts();
			},

      setupTicketListeners: function(){
        this.$store.state.eventBus.$on(this.$store.getters.getMessageChannelPrefix + '.ticketUpdated.' + this.id,this.ticketNotification);
        this.$store.state.eventBus.$on(this.$store.getters.getMessageChannelPrefix + '.ticketComment.' + this.id,this.ticketNotification);
        this.$store.state.eventBus.$on(this.$store.getters.getMessageChannelPrefix + '.ticketDeleted.' + this.id,this.ticketNotification);
      },

      stopTicketListeners: function(){
        this.$store.state.eventBus.$off(this.$store.getters.getMessageChannelPrefix + '.ticketUpdated.' + this.id,this.ticketNotification);
        this.$store.state.eventBus.$off(this.$store.getters.getMessageChannelPrefix + '.ticketComment.' + this.id,this.ticketNotification);
        this.$store.state.eventBus.$off(this.$store.getters.getMessageChannelPrefix + '.ticketDeleted.' + this.id,this.ticketNotification);
      },


      ticketNotification: function(event){
        if(event.channel.includes('ticketUpdated')){
          this.ticket = event.message;
        }else if(event.channel.includes('ticketComment')){
          this.ticket = event.message.ticket;
          this.commentAdded(event.message.comments[0]);
        }else if(event.channel.includes('ticketDeleted')){
          this.$emit('back');
        }
      },

      commentAdded: function(comment){
        let ix = this.comments.findIndex(c => c.id === comment.id);
        if(ix === -1){
          this.comments.unshift(comment);
        }
      },

			getTicketDetail: function() {
				this.ticketService.getTicketDetail(this.id).then((res) => {
					this.ticket = res.data.ticket;
					this.comments = res.data.comments;

          if(this.ticket.unreadClient){
            this.ticketService.markAsRead(this.id);
          }
				});
			},

			addComment: function() {
				this.$store.commit('startLoading');
				this.ticketService
					.createComment(this.id, this.comment, this.attachments)
					.then((res) => {
            this.commentAdded(res.data);
						this.comment = null;
						this.attachments.splice(0, this.attachments.length);
					})
					.finally(() => this.$store.commit('stopLoading'));
			},

			getContacts: function() {
				this.projectService.getAllAssignableUsers().then((res) => {
					this.contacts.splice(0, this.contacts.length);
					this.contacts.push(...res.data);
				});
			},

			getCommentBy: function(id) {
				let contact = this.contacts.find((c) => c.userId === id);
				if (contact) {
					return contact.firstName + ' ' + contact.lastName;
				} else {
					return ' -- ';
				}
			},

			commenterInitials: function(id) {
				const name = this.getCommentBy(id);
				if (!name || name === ' -- ') return '—';
				return name
					.split(' ')
					.filter(Boolean)
					.slice(0, 2)
					.map((part) => part.charAt(0))
					.join('')
					.toUpperCase();
			},

			downloadFile: function(s3File, download = true) {
				this.ticketService.getSignedUrl(this.id, s3File.fileName).then((res) => {
					let signedUrl = res.data;

					if (download == false) {
						window.open(signedUrl, '_blank');
					} else {
						axios({
							url: signedUrl, //your url
							method: 'GET',
							responseType: 'blob', // important
						}).then((response) => {
							const url = window.URL.createObjectURL(new Blob([response.data]));
							const link = document.createElement('a');
							link.href = url;
							link.setAttribute('download', s3File.fileName); //or any other extension
							document.body.appendChild(link);
							link.click();
						});
					}
				});
			},
		},

		computed: {
			hasFormAnswers: function() {
				return Boolean(this.ticket.formData && this.ticket.formData.answers && this.ticket.formData.answers.length);
			},

			requester: function() {
				return this.contacts.find((c) => c.userId === this.ticket.clientContactId);
			},

			ticketType: function() {
				if (this.ticketTypes) {
					let result = this.ticketTypes.find((t) => t.id === this.ticket.type);
					if (result) {
						return result;
					}
				}

				return {};
			},
		},
	};
</script>

<style scoped lang="scss">
	.request-detail-page {
		height: 100%;
		padding: 0 16px 28px;
		overflow-y: auto;
		color: var(--portal-text);
		text-align: left;
	}

	.request-detail-header {
		position: sticky;
		z-index: 3;
		top: 0;
		padding: 14px 0 18px;
		border-bottom: 1px solid var(--portal-border);
		background: var(--portal-canvas);
	}

	.request-back {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 5px 7px 5px 3px;
		border: 0;
		border-radius: var(--brand-button-radius, 8px);
		background: transparent;
		color: var(--portal-muted);
		font: inherit;
		font-size: 12px;
		font-weight: 650;
		cursor: pointer;
	}

	.request-back:hover {
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.request-back:focus-visible,
	.add-comment-action:focus-visible,
	.comment-attachment:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}

	.request-title-row {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 16px;
		margin-top: 10px;
	}

	.request-title {
		min-width: 0;
	}

	.request-number {
		display: block;
		margin-bottom: 3px;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 700;
	}

	.request-title h1 {
		margin: 0;
		color: var(--portal-text);
		font-size: clamp(22px, 3vw, 30px);
		font-weight: 700;
		line-height: 1.2;
		letter-spacing: -0.025em;
	}

	.request-detail-grid {
		display: flex;
		flex-direction: column;
		gap: 18px;
		padding-top: 18px;
	}

	.request-conversation {
		width: 100%;
		min-width: 0;
		display: grid;
		gap: 14px;
	}

	.comment-composer,
	.comment-card,
	.request-meta-card,
	.request-answer-section {
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 14px);
		background: var(--portal-surface);
	}

	.comment-composer {
		padding: 16px;
	}

	.composer-label {
		display: block;
		margin-bottom: 8px;
		color: var(--portal-text);
		font-size: 13px;
		font-weight: 680;
	}

	.comment-composer ::v-deep .v-input__slot {
		border-radius: var(--brand-field-radius, 11px);
		background: var(--v-gray_10-base);
	}

	.comment-composer ::v-deep fieldset {
		border-color: var(--portal-border);
	}

	.composer-footer {
		display: flex;
		align-items: stretch;
		flex-direction: column;
		gap: 10px;
		margin-top: 12px;
	}

	.composer-file-input {
		width: 100%;
		min-width: 0;
	}

	.composer-file-input ::v-deep .file-input-trigger {
		min-height: 48px;
	}

	.add-comment-action {
		min-height: 38px;
		align-self: flex-end;
		flex: 0 0 auto;
		padding: 0 14px;
		border: 0;
		border-radius: var(--brand-button-radius, 10px);
		background: var(--v-primary-base);
		color: var(--v-white-base);
		font: inherit;
		font-size: 12px;
		font-weight: 680;
		cursor: pointer;
	}

	.add-comment-action:disabled {
		background: var(--v-gray_30-base);
		color: var(--portal-muted);
		cursor: default;
	}

	.comment-stream {
		display: grid;
		gap: 10px;
	}

	.comment-card {
		padding: 16px;
	}

	.comment-header {
		display: flex;
		align-items: flex-start;
		gap: 10px;
		padding-bottom: 12px;
		border-bottom: 1px solid var(--v-gray_20-base);
	}

	.comment-avatar {
		width: 34px;
		height: 34px;
		flex: 0 0 34px;
		display: grid;
		place-items: center;
		border-radius: 11px;
		background: color-mix(in srgb, var(--v-primary-base) 10%, var(--portal-surface));
		color: var(--v-primary-base);
		font-size: 11px;
		font-weight: 750;
	}

	.comment-header > div {
		min-width: 0;
		display: grid;
		gap: 1px;
	}

	.comment-header strong {
		font-size: 13px;
		font-weight: 680;
	}

	.comment-header span {
		color: var(--portal-muted);
		font-size: 11px;
	}

	.comment-body {
		padding-top: 13px;
		color: var(--portal-text);
		font-size: 14px;
		line-height: 1.6;
		white-space: pre-wrap;
		overflow-wrap: anywhere;
	}

	.rich-comment {
		white-space: normal;
	}

	.comment-attachments {
		display: flex;
		flex-wrap: wrap;
		gap: 7px;
		margin-top: 12px;
	}

	.comment-attachment {
		min-height: 30px;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 0 9px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-button-radius, 9px);
		background: var(--v-gray_10-base);
		color: var(--portal-text);
		font: inherit;
		font-size: 11px;
		cursor: pointer;
	}

	.comment-attachment:hover {
		border-color: color-mix(in srgb, var(--v-primary-base) 25%, var(--portal-border));
		color: var(--v-primary-base);
	}

	.request-sidebar {
		width: 100%;
		order: -1;
		display: grid;
		gap: 12px;
	}

	.request-meta-card {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		padding: 6px 15px;
	}

	.request-meta-row {
		display: grid;
		gap: 4px;
		padding: 10px 12px;
		border-right: 1px solid var(--v-gray_20-base);
	}

	.request-meta-row:last-child {
		border-right: 0;
	}

	.request-meta-row span,
	.request-answer span {
		color: var(--portal-muted);
		font-size: 11px;
	}

	.request-meta-row strong {
		color: var(--portal-text);
		font-size: 12px;
		font-weight: 650;
		text-align: left;
		overflow-wrap: anywhere;
	}

	.request-answer-card {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
		padding: 6px 15px;
		border-top: 1px solid var(--v-gray_20-base);
	}

	.request-answer-section {
		overflow: hidden;
	}

	.request-answer-toggle {
		width: 100%;
		min-height: 48px;
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 0 14px;
		border: 0;
		background: transparent;
		color: var(--portal-text);
		font: inherit;
		font-size: 12px;
		font-weight: 680;
		text-align: left;
		cursor: pointer;
	}

	.request-answer-toggle:hover,
	.request-answer-toggle:focus-visible {
		outline: 0;
		background: var(--v-gray_20-base);
	}

	.request-answer-toggle > :first-child {
		flex: 1;
	}

	.request-answer-count {
		min-width: 24px;
		height: 22px;
		display: inline-grid;
		place-items: center;
		padding: 0 7px;
		border-radius: 999px;
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
		font-size: 11px;
		font-weight: 700;
	}

	.request-answer {
		display: grid;
		gap: 4px;
		padding: 10px 12px;
		border-right: 1px solid var(--v-gray_20-base);
	}

	.request-answer:last-child {
		border-right: 0;
	}

	.request-answer strong {
		color: var(--portal-text);
		font-size: 13px;
		font-weight: 600;
		line-height: 1.45;
	}

	@media (max-width: 599px) {
		.request-detail-page {
			padding: 0 12px 20px;
		}

		.request-detail-header {
			padding-top: 10px;
		}

		.request-title-row {
			align-items: flex-start;
		}

		.request-title h1 {
			font-size: 21px;
		}

		.request-meta-card,
		.request-answer-card {
			grid-template-columns: 1fr;
		}

		.request-meta-row {
			grid-template-columns: minmax(82px, auto) minmax(0, 1fr);
			gap: 12px;
			border-right: 0;
			border-bottom: 1px solid var(--v-gray_20-base);
		}

		.request-meta-row:last-child {
			border-bottom: 0;
		}

		.request-meta-row strong {
			text-align: right;
		}

		.request-answer {
			border-right: 0;
			border-bottom: 1px solid var(--v-gray_20-base);
		}

		.request-answer:last-child {
			border-bottom: 0;
		}

		.composer-footer {
			align-items: stretch;
		}

		.add-comment-action {
			width: 100%;
			align-self: stretch;
		}
	}
</style>
